import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    const savedCart = localStorage.getItem('cm-peptides-cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });
  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem('cm-peptides-cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (product, quantity = 1) => {
    // Safety net: never add an out-of-stock product to the cart
    if (product?.stockStatus === 'out_of_stock') {
      return false;
    }
    const cartKey = (p) => (p.variant ? `${p.id}-${p.variant}` : `${p.id}`);
    const newKey = cartKey(product);
    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => cartKey(item) === newKey);

      if (existingItem) {
        return prevItems.map(item =>
          cartKey(item) === newKey
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }

      return [...prevItems, { ...product, quantity }];
    });
    return true;
  };

  const removeFromCart = (productId, variant) => {
    const targetKey = variant ? `${productId}-${variant}` : `${productId}`;
    setCartItems(prevItems =>
      prevItems.filter(item => (item.variant ? `${item.id}-${item.variant}` : `${item.id}`) !== targetKey)
    );
  };

  const updateQuantity = (productId, quantity, variant) => {
    if (quantity <= 0) {
      removeFromCart(productId, variant);
      return;
    }
    const targetKey = variant ? `${productId}-${variant}` : `${productId}`;
    setCartItems(prevItems =>
      prevItems.map(item =>
        (item.variant ? `${item.id}-${item.variant}` : `${item.id}`) === targetKey
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount,
    isCartOpen,
    setIsCartOpen
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
